<script setup>
import { onBeforeRouteUpdate } from "vue-router";
import { useThreadsContextStore } from "@/stores/threads";
import { useUserStore } from "@/stores/user";
import { useNavigation } from "@/composables/useNavigation";
import ThreadDetailsHeader from "@/components/ThreadDetailsHeader.vue";
import ThreadsDetailsInteractionList from "@/components/ThreadsDetailsInteractionList.vue";
import ThreadDetailsTab from "@/components/ThreadDetailsTab.vue";
import { useToast } from "vue-toastification";
import { useInteractionTrainingStore } from "@/stores/interactionTraining";
import { useInteractionDetailStore } from "@/stores/interactionDetail";
import { useWfbChatWidgetStore } from "@/stores/wfbChatWidget";
import WfbChatWidget from "@/components/WfbChatWidget.vue";
import TrainingDialogModal from "@/components/TrainingDialogModal.vue";

const SUMMARY_MARGIN_BOTTOM = 10
const SUMMARY_INIT_HEIGHT = 145
const MAIN_CONTAINER_PADDING = 24

const toast = useToast();
const threadsStore = useThreadsContextStore();
const userStore = useUserStore()
const interactionDetailStore = useInteractionDetailStore();
const interactionTrainingStore = useInteractionTrainingStore();
const wfbChatWidgetStore = useWfbChatWidgetStore();
const { routeParams, queryParams } = useNavigation();

const headerContainerRef = ref(null);
const threadDetailsRef = ref(null);
const mainContainerRef = ref(null);


// get a calculated value of being able to read only
const readOnlyMode = computed(() => !userStore.perms.map((permsObj) => permsObj.code).includes("view-inbox-page"))

watch(() => headerContainerRef.value?.height,
  (h) => {
    if (!threadDetailsRef.value) return;

    if (h) {
      threadDetailsRef.value.style.maxHeight = `calc(100vh - ${h}px)`;
      mainContainerRef.value.style.height = `calc(100vh - ${h}px - ${MAIN_CONTAINER_PADDING}px - ${MAIN_CONTAINER_PADDING}px)`
    } else {
      // fallback heights
      threadDetailsRef.value.style.maxHeight = `calc(100vh - ${SUMMARY_INIT_HEIGHT}px)`;
      mainContainerRef.value.style.height = `calc(100vh - ${SUMMARY_INIT_HEIGHT}px - ${MAIN_CONTAINER_PADDING}px - ${MAIN_CONTAINER_PADDING}px)`
    }
  }, {immediate: true})

const cachedInteraction = ref(null);
let lastId = null;

const threadId = ref(routeParams.value.id);
const thread = computed(() => threadsStore.getThreadById(threadId.value) || {});

const showTrainingOnLoad = computed(
  () => (
    !!(queryParams.value?.openTraining) &&
    !!(queryParams.value?.focusInteraction)
  )
);

const findInteraction = (interactionId) => {
  const t = thread.value;
  return t.interactions.find((i) => i.id === interactionId) || null;
}

const ticketInteraction = computed(() => {
  const t = thread.value;
  const id = t.ticket_interaction_id;

  if (!id) {
    cachedInteraction.value = null;
    lastId = null;
    return null;
  }

  if (id === lastId && cachedInteraction.value) {
    return cachedInteraction.value;
  }

  lastId = id;
  cachedInteraction.value = findInteraction(id);
  return cachedInteraction.value;
});

const summaryInput = ref("");

watch(
  thread,
  (t) => {
    summaryInput.value =
      t.user_input_summary?.summary ||
      t.thread_summary?.summary ||
      "Add your summary here";
  },
  { immediate: true }
);

watch(
  showTrainingOnLoad,
  (doShow) => {
    if (!doShow) {
      interactionTrainingStore.resetActiveInteraction();
    }
    else {
      interactionTrainingStore.setActiveInteraction(
        queryParams.value.focusInteraction
      );
    }
  },
  { immediate: true }
)

const fetchThread = async (newThreadId) => {
  if (newThreadId) {
    // verify this is a thread id
    const trueThreadId = await threadsStore.getThreadIdForId(newThreadId);
    // this means newThreadId is the InteractionId due to our migration work
    if (trueThreadId !== newThreadId) {
      const url = new URL(document.URL);
      const migrateTraining = url.searchParams.has("initiateTraining");
      console.log("migrateTraining", migrateTraining, url);

      // redirect to trueThreadId
      url.pathname = url.pathname.replace(newThreadId, trueThreadId);

      if (migrateTraining === true) {
        url.searchParams.delete("initiateTraining");
        url.searchParams.delete("trainingId");

        url.searchParams.set("openTraining", true);
        url.searchParams.set("focusInteraction", newThreadId);
      }

      document.location = url.toString();
    }

    console.log("fetching new thread: ", newThreadId);
    await threadsStore.fetchThread(newThreadId);
  }
};

watch(threadId, fetchThread, { immediate: true });

onBeforeRouteUpdate((to) => {
  // Widget sessions are scoped to the current thread.
  if (to.params.id !== threadId.value) {
    wfbChatWidgetStore.closeWidget();
  }
  threadId.value = to.params.id;
});

provide("THREAD", thread);
provide("TICKET_INTERACTION", ticketInteraction);
provide("READ_ONLY_MODE", readOnlyMode)

onUnmounted(() => {
  interactionDetailStore.isEditorOpen = false;
  interactionDetailStore.editorValues = {};
  wfbChatWidgetStore.closeWidget();
});
</script>

<template>
  <div class="fullscreen-card position-fixed top-0 left-0 vh-100 bg-white">
    <ThreadDetailsHeader class="header-container" ref="headerContainerRef" />
    <div class="thread-details" ref="threadDetailsRef">
      <div class="main-container" ref="mainContainerRef">
        <ThreadsDetailsInteractionList
          class="thread-list"
        />
        <ThreadDetailsTab class="thread-tabs" />
        <TrainingDialogModal
          v-if="interactionTrainingStore.isTrainingPopupOpen && interactionTrainingStore.activeInteractionId"
          :interactionId="interactionTrainingStore.activeInteractionId"
        />
        <WfbChatWidget v-if="wfbChatWidgetStore.isWidgetOpen" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$--thread-list-min-width: 550px;
// $--thread-list-min-height: 350px;
$--summary-margin-bottom: 10px;

.main-container {
  display: flex;
  gap: 2rem;
  min-width: 0;
  overflow-x: auto;
}

.thread-tabs {
  flex: 1 1 auto;
  min-width: 400px;
}

.thread-details {
  background-color: $neutral-50;
  padding: 24px 64px 24px 64px; /* top/bottom match header gap; keep x padding */
  display: flex;
  flex-direction: column;
}

// Need to give thread list a height here
.thread-list {
  flex: 0 0 $--thread-list-min-width;
  min-width: $--thread-list-min-width;
  // min-height: $--thread-list-min-height;

  :deep(.scroll-wrapper) {
    // height: 100%;
    // min-height: $--thread-list-min-height;
  }
}

.thread-details-component-container {
  position: relative;
  padding: 1.5rem 2rem 2rem 1.5rem;
  border-radius: 8px;
  border: 1px solid $neutral-150;
  background: $neutral-0;
  overflow-y: auto;
  font-size: $font-size-sm;
}

.summary-heading {
  font-size: 1rem;
  font-weight: 700;
  color: $neutral-450;
  margin-bottom: 12px;
  line-height: 1.3;
  border-bottom: 1px solid $neutral-150;
}

.fullscreen-card {
  z-index: 1;
  width: calc(100% - 5rem);
  max-width: 100%;
}

.summary-block {
  cursor: pointer;
}

.title-label {
  white-space: pre-wrap;
  cursor: pointer;
}

.summary-textarea {
  resize: vertical;
  white-space: pre-wrap;
}

.save-button {
  margin-top: 5px;
  background-color: var(--primary-250);
}
</style>
